<script setup lang="ts">
import type { ResponseUpload } from '~/utils/receipt-model/ReceiptModel'
import IconCheck from '~icons/material-symbols/check-circle-outline'
import FileUpload from '~/components/input/FileUpload/index.vue'
import { InvoiceStatus } from '~/constants/receiptModel'

const props = defineProps<{
  id: string
  label: string
  hint?: string
  approvedLabel: string
  currentDocument?: Readonly<ResponseUpload>
  formError?: string
  loading: boolean
  disabled: boolean
}>()

const emit = defineEmits<{
  change: [file: File | null]
}>()

const { t } = useI18n()

const fileValue = computed<Record<string, unknown> | null>(() =>
  props.currentDocument ? { ...props.currentDocument } : null,
)

const errorMessage = computed<string | undefined>(() => {
  if (props.loading)
    return undefined
  if (!props.currentDocument)
    return props.formError || undefined
  if (props.currentDocument.status === InvoiceStatus.DENIED)
    return props.currentDocument.reason || t('defaultDenialReason')
  return undefined
})

const labelId = computed(() => `label-file-upload-${props.id}`)

const isApproved = computed(() => props.currentDocument?.status === InvoiceStatus.APPROVED)
</script>

<template>
  <div class="invoice-upload-field">
    <span :id="labelId" class="invoice-upload-field__label">{{ label }}</span>

    <FileUpload
      :id="id"
      :labelled-by="labelId"
      :model-value="fileValue"
      accept=".pdf"
      :max-size-mb="10"
      :file-name="currentDocument?.filename || undefined"
      :view-url="currentDocument?.url_inline || undefined"
      :download-url="currentDocument?.url || undefined"
      :preview-mime-type="currentDocument?.mimetype || undefined"
      preview-id="modal-preview-file"
      :loading="loading"
      :disabled="disabled"
      :error="errorMessage"
      @update:model-value="emit('change', $event)"
    />

    <span v-if="hint && !errorMessage" class="invoice-upload-field__hint">
      {{ hint }}
    </span>

    <div v-if="isApproved" class="invoice-upload-field__approved">
      <span class="text-feedback-positive-pure text-micro">{{ approvedLabel }}</span>
      <IconCheck class="text-feedback-positive-pure" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.invoice-upload-field {
  @apply flex flex-col gap-1 w-full;

  &__label {
    @apply fonts-body-medium-bold text-neutral-low-dark;
  }

  &__hint {
    @apply block m-0 fonts-body-small-regular text-neutral-low-light;
  }

  &__approved {
    @apply flex items-center gap-1;
  }
}
</style>
